import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../../components/Admin/Sidebar";
import styles from "./RoomManagement.module.css";
import Header from "../../components/Admin/Header";
import api from "../../api/axois";
import { getImageUrl } from "../../utils/imageUtils";
import { ADMIN_ROWS_PER_PAGE } from "../../constants/admin";

const statuses = ["All", "Active", "Inactive"];

const RoomManagement = () => {
  const [roomsData, setRoomsData] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All");
  const [sort, setSort] = useState("Most Recent");
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingRoom, setDeletingRoom] = useState(null);
  const [deleteSuccess, setDeleteSuccess] = useState("");
  const [actionError, setActionError] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [reloadToken, setReloadToken] = useState(0);
  // Unavailability modal state
  const [unavailModal, setUnavailModal] = useState(null); // room object
  const [unavailUntil, setUnavailUntil] = useState("");
  const [unavailLoading, setUnavailLoading] = useState(false);
  const [unavailError, setUnavailError] = useState("");
  const rowsPerPage = ADMIN_ROWS_PER_PAGE;

  const handleDeleteRoom = async () => {
    if (!deletingRoom) return;
    try {
      await api.delete(`/rooms/${deletingRoom.id}`);
      setRoomsData((prev) => prev.filter(r => r.id !== deletingRoom.id));
      setDeleteModalOpen(false);
      setDeleteSuccess("Room deleted successfully.");
      setTimeout(() => setDeleteSuccess(""), 3000);
      setDeletingRoom(null);
    } catch (err) {
      setActionError(err?.response?.data?.message || "Failed to delete room.");
      setDeleteModalOpen(false);
      setDeletingRoom(null);
    }
  };

  const openUnavailModal = (room) => {
    setUnavailModal(room);
    // Default to 24 hours from now
    const d = new Date(Date.now() + 24 * 60 * 60 * 1000);
    setUnavailUntil(d.toISOString().slice(0, 16));
    setUnavailError("");
  };

  const handleSetUnavailable = async () => {
    if (!unavailUntil) { setUnavailError("Please select an end date/time."); return; }
    if (new Date(unavailUntil) <= new Date()) { setUnavailError("End time must be in the future."); return; }
    setUnavailLoading(true);
    setUnavailError("");
    try {
      await api.put(`/rooms/${unavailModal.id}/availability`, {
        is_available: false,
        unavailable_until: unavailUntil,
      });
      setRoomsData((prev) =>
        prev.map((r) =>
          r.id === unavailModal.id
            ? { ...r, is_available: false, unavailable_until: new Date(unavailUntil).toISOString() }
            : r
        )
      );
      setUnavailModal(null);
    } catch (err) {
      setUnavailError(err?.response?.data?.message || "Failed to update room.");
    } finally {
      setUnavailLoading(false);
    }
  };

  const handleRestoreRoom = async (room) => {
    try {
      await api.put(`/rooms/${room.id}/availability`, { is_available: true });
      setRoomsData((prev) =>
        prev.map((r) =>
          r.id === room.id ? { ...r, is_available: true, unavailable_until: null } : r
        )
      );
    } catch (err) {
      setActionError(err?.response?.data?.message || "Failed to restore room.");
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFetchError("");
    Promise.all([
      api.get("/roommanagement"),
      api.get("/admin/room-types"),
    ])
      .then(([roomsRes, typesRes]) => {
        if (cancelled) return;
        setRoomsData(roomsRes.data.rooms || []);
        setRoomTypes(typesRes.data.roomTypes || []);
      })
      .catch((err) => {
        if (cancelled) return;
        setFetchError(err?.response?.data?.message || "Failed to load rooms. Please try again.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  // Get active room type IDs and names
  const activeRoomTypeIds = roomTypes.filter(rt => rt.is_active).map(rt => rt.id);
  const activeRoomTypeNames = roomTypes.filter(rt => rt.is_active).map(rt => rt.title);

  const filteredData = roomsData
    .filter((r) => {
      // Only show rooms whose roomType is active
      let typeId = r.roomTypeId || r.roomType?.id;
      let typeName = r.roomType?.title || r.roomType || r.roomTypeName;
      let isActiveType = false;
      if (typeId && activeRoomTypeIds.includes(typeId)) isActiveType = true;
      else if (typeName && activeRoomTypeNames.includes(typeName)) isActiveType = true;
      if (!isActiveType) return false;
      // Filter by status
      if (status === "Active" && !r.is_active) return false;
      if (status === "Inactive" && r.is_active) return false;
      // Filter by search
      const matchesSearch = (r.room_number && r.room_number.toString().toLowerCase().includes(search.toLowerCase())) ||
        (r.roomID != null && r.roomID.toString().toLowerCase().includes(search.toLowerCase()));
      return matchesSearch;
    })
    .sort((a, b) => {
      if (sort === "Most Recent") return b.id - a.id;
      if (sort === "Oldest") return a.id - b.id;
      return 0;
    });

  const paginated = filteredData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  const navigate = useNavigate();
  const handleView = (id) => {
    navigate(`/admin/room-details/${id}`);
  };

  return (
    <div className={styles.container}>
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className={styles.content}>
        <Header pageTitle="Room Management" onMenuClick={() => setSidebarOpen(true)} />

        <div className={styles.reservations}>
          {deleteSuccess && (
            <div className={styles.successBanner} role="status">
              {deleteSuccess}
            </div>
          )}
          {actionError && (
            <div className={styles.errorBanner} role="alert">
              {actionError}
            </div>
          )}
          <h3>
            All Rooms <span className={styles.new}>{roomsData.length} Total rooms</span>
          </h3>
          <p className={styles.sectionSubtext}>
            Individual room units and their live availability. To edit pricing, amenities, or photos for a category, use Room Types instead.
          </p>
          <div className={styles.controls}>
            <label htmlFor="room-search" className={styles.srOnly}>Search Room Number</label>
            <input
              id="room-search"
              type="text"
              placeholder="Search Room Number"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
            />
            <label htmlFor="room-status" className={styles.srOnly}>Filter by status</label>
            <select
              id="room-status"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setCurrentPage(1);
              }}
            >
              {statuses.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
            <label htmlFor="room-sort" className={styles.srOnly}>Sort order</label>
            <select
              id="room-sort"
              value={sort}
              onChange={(e) => {
                setSort(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option>Most Recent</option>
              <option>Oldest</option>
            </select>
          </div>

          {loading ? (
            <div className={styles.loadingState}>Loading rooms…</div>
          ) : fetchError ? (
            <div className={styles.fetchError}>
              {fetchError}
              <button className={styles.retryBtn} onClick={() => setReloadToken((t) => t + 1)}>
                Retry
              </button>
            </div>
          ) : (
          <>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Room Type</th>
                  <th>Availability</th>
                  <th>Room ID</th>
                  <th>Price</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {paginated.length === 0 ? (
                  <tr>
                    <td colSpan="5" className={styles.noData}>
                      No rooms found.
                    </td>
                  </tr>
                ) : (
                  paginated.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <div className={styles.roomInfo}>
                          <img
                            src={getImageUrl(row.image)}
                            alt="Room"
                          />
                          <span>{row.roomType?.title || row.roomType || row.roomTypeName || ""}</span>
                        </div>
                      </td>
                      <td>
                        {row.is_available ? (
                          <span className={`${styles.status} ${styles.available}`}>Available</span>
                        ) : (
                          <span className={`${styles.status} ${styles.unavailable}`}>
                            Unavailable
                            {row.unavailable_until && (
                              <span className={styles.unavailUntil}>
                                {" "}until {new Date(row.unavailable_until).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}
                              </span>
                            )}
                          </span>
                        )}
                      </td>
                      <td>
                        <strong>Room {row.room_number || row.roomID}</strong>
                      </td>
                      <td>{row.cost}</td>
                      <td>
                        <div className={styles.actions}>
                          <button className={styles.view} onClick={() => handleView(row.id)}>
                            View Details →
                          </button>
                          {row.is_available ? (
                            <button
                              className={styles.unavailBtn}
                              onClick={() => openUnavailModal(row)}
                            >
                              Set Unavailable
                            </button>
                          ) : (
                            <button
                              className={styles.restoreBtn}
                              onClick={() => handleRestoreRoom(row)}
                            >
                              Restore
                            </button>
                          )}
                          <button className={styles.delete} onClick={() => { setDeletingRoom(row); setDeleteModalOpen(true); }}>
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button
                className={styles.pageButton}
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
              >
                ‹ Previous
              </button>
              {Array.from({ length: totalPages }, (_, n) => (
                <button
                  key={n}
                  onClick={() => setCurrentPage(n + 1)}
                  className={`${styles.pageButton} ${currentPage === n + 1 ? styles.active : ''}`}
                >
                  {n + 1}
                </button>
              ))}
              <button
                className={styles.pageButton}
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next ›
              </button>
            </div>
          )}
          </>
          )}
        </div>
      </div>
      {sidebarOpen && (
        <div className={styles.overlay} onClick={() => setSidebarOpen(false)} />
      )}
      {deleteModalOpen && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="delete-room-heading">
            <h3 id="delete-room-heading">Are you sure you want to delete this room?</h3>
            <p>This action cannot be undone.</p>
            <div className={styles.modalActions}>
              <button className={styles.cancelBtn} onClick={() => setDeleteModalOpen(false)} disabled={!deletingRoom}>Cancel</button>
              <button className={styles.delete} onClick={handleDeleteRoom} disabled={!deletingRoom}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {unavailModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="unavail-room-heading">
            <h3 id="unavail-room-heading" className={styles.modalHeading}>Set Room Unavailable</h3>
            <p className={styles.modalSubtext}>
              Room <strong>{unavailModal.room_number || unavailModal.roomID}</strong> will be marked as unavailable until the selected date and time, then automatically restored.
            </p>
            <label className={styles.fieldLabel}>
              Available again from:
              <input
                type="datetime-local"
                className={styles.dateInput}
                value={unavailUntil}
                min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
                onChange={(e) => { setUnavailUntil(e.target.value); setUnavailError(""); }}
              />
            </label>
            {unavailError && <p className={styles.fieldError}>{unavailError}</p>}
            <div className={styles.modalActions}>
              <button
                className={styles.cancelBtn}
                onClick={() => setUnavailModal(null)}
                disabled={unavailLoading}
              >
                Cancel
              </button>
              <button
                className={styles.confirmUnavailBtn}
                onClick={handleSetUnavailable}
                disabled={unavailLoading}
              >
                {unavailLoading ? "Saving..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoomManagement;
